import { useCallback, useEffect, useMemo } from "react";
import { useFlashcardActions } from "./useFlashcardActions";
import { useFlashcardEffects } from "./useFlashcardEffects";
import { resolveInitialFlashcardIndex } from "./flashcardResumeIndex";
import { useFlashcardSession } from "./useFlashcardSession";
import { useFlashcardTimer } from "./useFlashcardTimer";
import { useGetFlashcards } from "./useGetFlashcards";

type UseFlashcardScreenControllerParams = {
  examId: number;
  gradeId: number;
  subtenantId: number;
  subjectId: number;
  chapterId: number;
  chapterName: string;
  topicId: number;
  topicName: string;
  flashcardTypeId: number;
};

const toFlashcardId = (id?: number | null) => {
  if (typeof id !== "number") {
    return null;
  }

  if (!Number.isFinite(id)) {
    return null;
  }

  return id;
};

export const useFlashcardScreenController = ({
  examId,
  gradeId,
  subtenantId,
  subjectId,
  chapterId,
  chapterName,
  topicId,
  topicName,
  flashcardTypeId,
}: UseFlashcardScreenControllerParams) => {
  const { flashcardData, flashcardProgressData, isLoading } = useGetFlashcards(
    examId,
    gradeId,
    subtenantId,
    subjectId,
    chapterId,
    topicId,
    flashcardTypeId,
  );

  const { state, initialize, updateCard, setIndex } = useFlashcardSession();
  const actions = useFlashcardActions(updateCard);
  const effects = useFlashcardEffects({
    examId,
    gradeId,
    subjectId,
    chapterId,
    chapterName,
    topicId: topicId,
    topicName,
  });
  const timer = useFlashcardTimer();

  useEffect(() => {
    if (flashcardData === undefined) {
      return;
    }
    initialize(flashcardData, flashcardProgressData);
  }, [flashcardData, flashcardProgressData, initialize]);

  const handleSetIndex = useCallback(
    (index: number) => {
      timer.resetTimer();
      setIndex(index);
    },
    [setIndex, timer],
  );

  const computeTopLevel = useCallback(
    (
      overrideId?: number,
      overrides?: { isCompleted?: boolean; isBookmarked?: boolean },
    ) => {
      const parentIsCompleted =
        state.flashcards.every((c) =>
          c.flashcardId === overrideId
            ? (overrides?.isCompleted ?? c.isCompleted)
            : c.isCompleted,
        ) && state.questionIds.length === 0;
      const parentIsBookmarked = state.flashcards.some((c) =>
        c.flashcardId === overrideId
          ? (overrides?.isBookmarked ?? c.isBookmarkSelected)
          : c.isBookmarkSelected,
      );
      return { parentIsCompleted, parentIsBookmarked };
    },
    [state.flashcards, state.questionIds],
  );

  const onBookmark = useCallback(
    (id?: number | null) => {
      const normalizedId = toFlashcardId(id);
      if (normalizedId === null) return;

      const card = state.flashcards.find((c) => c.flashcardId === normalizedId);
      if (!card) return;
      const newIsBookmarked = !card.isBookmarkSelected;
      actions.toggleBookmark(normalizedId);
      const { parentIsCompleted, parentIsBookmarked } = computeTopLevel(
        normalizedId,
        {
          isBookmarked: newIsBookmarked,
        },
      );
      effects.bookmark(
        card,
        newIsBookmarked,
        parentIsCompleted,
        parentIsBookmarked,
      );
    },
    [actions, effects, state.flashcards, computeTopLevel],
  );

  const onLike = useCallback(
    (id?: number | null) => {
      const normalizedId = toFlashcardId(id);
      if (normalizedId === null) return;

      const card = state.flashcards.find((c) => c.flashcardId === normalizedId);
      if (!card) return;
      const newIsLiked = !card.isLiked;
      actions.onLike(normalizedId);
      const { parentIsCompleted, parentIsBookmarked } = computeTopLevel();
      effects.like(
        card,
        newIsLiked,
        false,
        parentIsCompleted,
        parentIsBookmarked,
      );
    },
    [actions, effects, state.flashcards, computeTopLevel],
  );

  const onDislike = useCallback(
    (id?: number | null) => {
      const normalizedId = toFlashcardId(id);
      if (normalizedId === null) return;

      const card = state.flashcards.find((c) => c.flashcardId === normalizedId);
      if (!card) return;
      const newIsDisLiked = !card.isDisLiked;
      actions.onDislike(normalizedId);
      const { parentIsCompleted, parentIsBookmarked } = computeTopLevel();
      effects.like(
        card,
        false,
        newIsDisLiked,
        parentIsCompleted,
        parentIsBookmarked,
      );
    },
    [actions, effects, state.flashcards, computeTopLevel],
  );

  const onCardViewed = useCallback(
    (flashcardId: number) => {
      const card = state.flashcards.find((c) => c.flashcardId === flashcardId);
      if (!card || card.isCompleted) return;
      const timeTakenInSecs = timer.getElapsedSeconds();
      const { parentIsCompleted, parentIsBookmarked } = computeTopLevel(
        flashcardId,
        {
          isCompleted: true,
        },
      );
      actions.markCompleted(flashcardId);
      effects.saveProgress(
        card,
        timeTakenInSecs,
        parentIsCompleted,
        parentIsBookmarked,
      );
    },
    [state.flashcards, timer, actions, effects, computeTopLevel],
  );

  const totalCards = state.flashcards.length;
  const currentIndex = state.currentIndex;
  const currentCard = state.flashcards[currentIndex];
  const completedCount = state.flashcards.filter((f) => f.isCompleted).length;
  const initialScrollIndex = useMemo(
    () => resolveInitialFlashcardIndex(state.flashcards),
    [state.flashcards],
  );

  return {
    flashcards: state.flashcards,
    currentIndex,
    totalCards,
    currentCard,
    setIndex: handleSetIndex,
    onBookmark,
    onLike,
    onDislike,
    isLoading,
    questionIds: state.questionIds,
    questionProgress: state.questionProgress,
    onCardViewed,
    completedCount,
    initialScrollIndex,
  };
};
